import type { ReactNode } from 'react';

export interface StatBarDatum {
  key: string;
  label: string;
  color: string;
  value: number;
  icon?: ReactNode;
}

interface StatBarsProps {
  rows: StatBarDatum[];
  /** Tailwind width for the label column (fits the longest label). */
  labelWidth?: string;
  /** Word after the count in the row aria-label, e.g. "issues", "open issues". */
  ariaSuffix?: string;
  variant?: 'default' | 'compact';
  compactLabelWidth?: number;
  className?: string;
}

/**
 * The one horizontal-bar primitive for the project chart cards
 * (Severity, Status, Issue Types, Issue Age). Row = label · proportional bar ·
 * count. Bars are sized against the row set's own max, so a single dominant
 * value (e.g. everything Open) fills its bar while the rest sit empty.
 */
export function StatBars({ rows, labelWidth = 'w-14', ariaSuffix = 'issues', variant = 'default', compactLabelWidth = 105, className = '' }: StatBarsProps) {
  const max = Math.max(...rows.map((r) => r.value), 1);

  if (variant === 'compact') {
    return <dl className={`grid ${className}`}>
      {rows.map(({ key, label, color, value, icon }) => <div key={key} data-stat-key={key} className="grid items-center gap-4" style={{ gridTemplateColumns: `${compactLabelWidth}px minmax(0, 1fr) minmax(20px, max-content)` }}>
        <dt className="flex items-center gap-2 text-xs text-gray-500">{icon}{label}</dt>
        <dd className="h-1.5 rounded bg-gray-100" aria-hidden="true">
          <span className="block h-full rounded" style={{ width: `${value / max * 100}%`, backgroundColor: color }} />
        </dd>
        <dd className="text-right text-xs font-semibold text-gray-700 tabular-nums">{value}</dd>
      </div>)}
    </dl>;
  }

  return (
    <div className="flex flex-col flex-1 gap-3 pt-6">
      {rows.map(({ key, label, color, value }) => {
        const pct = (value / max) * 100;
        return (
          <div key={key} className="flex-1 flex items-center gap-3" aria-label={`${label}: ${value} ${ariaSuffix}`}>
            <span className={`text-xs text-gray-500 flex-shrink-0 whitespace-nowrap ${labelWidth}`}>{label}</span>
            <div className="flex-1 h-6 rounded-full bg-gray-100 overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${value > 0 ? Math.max(pct, 6) : 0}%`, backgroundColor: color }}
              />
            </div>
            <span className="text-xs font-semibold text-gray-700 w-7 text-right flex-shrink-0 tabular-nums">{value}</span>
          </div>
        );
      })}
    </div>
  );
}
